import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksWithToleranceSchema } from "../machine-with-corrugate-tracks";

const smartScalingBarcodeGeneratorSchema = z.object({
	enabled: z.boolean(),
	minBarcodeValue: z.number().int(),
	maxBarcodeValue: z.number().int(),
	barcodeType: z.number().int(),
	barcodeOrientation: z.number().int(),
	minimumPrintedBarcodeDigits: z.number().int(),
	zplBarcodeModuleWidth: z.number().int(),
	dotsPerInch: z.number().int(),
});

const printImageConfigurationSchema = z.object({
	smartScalingBarcodeGenerator: smartScalingBarcodeGeneratorSchema,
});

export const x5MachineSchema = machineWithCorrugateTracksWithToleranceSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.X5),
	printerIp: z.string().nullable().optional(),
	printerPort: z.number().optional(),
	settings: z.object({
		hasEfiPrinter: z.boolean(),
		hasFlapsDownModule: z.boolean(),
		designId: z.number(),
		printerMode: z.number().optional(),
	}),
	isDoubleTampPrinter: z.boolean(),
	printImageConfiguration: printImageConfigurationSchema,
});

// TODO @eb remove this function when the X5 aligns with our decided upon schemas
export const prepareX5MachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;
	const securePrintConfigurationInput = input.securePrintConfiguration as { delayLabelRetrieval?: boolean } | undefined;
	let printImageConfiguration = input.printImageConfiguration as PrintImageConfiguration;

	if (!printImageConfiguration) {
		printImageConfiguration = {
			smartScalingBarcodeGenerator: {
				enabled: false,
				minBarcodeValue: 0,
				maxBarcodeValue: 2147483647,
				barcodeType: 0,
				barcodeOrientation: 0,
				minimumPrintedBarcodeDigits: 1,
				zplBarcodeModuleWidth: 4,
				dotsPerInch: 300,
			},
		};
	}

	const iotDevice = {
		hubName: input.iotHubName ?? "",
		deviceId: input.iotEdgeDeviceId ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.edgeModuleVersion ?? "",
		lastProofOfLife: input.proofOfLifeUpdated,
	};

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t, i) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			let assignedMaterial = track.loadedCorrugate;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown>;

				assignedMaterial = {
					id: material.id ?? "00000000-0000-0000-0000-000000000000",
					alias: material.alias ?? "",
					width: material.width ?? 0,
					thickness: material.thickness ?? 0,
					quality: material.quality ?? 0,
				};
			} else {
				// If assignedMaterial is null, create default
				assignedMaterial = {
					id: "00000000-0000-0000-0000-000000000000",
					alias: "",
					width: 0,
					thickness: 0,
					quality: 0,
				};
			}

			return {
				number: track.trackNumber ?? 0,
				isEmpty: track.isTrackEmpty ?? false,
				assignedMaterial,
				corrugateTolerance: input.corrugateWidthTolerance ?? 0,
				// @ts-expect-error ts2339
				width: Array.isArray(input.settings?.configuredTrackWidths)
					? // @ts-expect-error ts2339
						(input.settings?.configuredTrackWidths[i] ?? 0)
					: 0,
			};
		});
	}

	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (input.hasPackTags === true) {
		const machineStopReason = input.stopReason as Record<string, unknown>;
		if (typeof machineStopReason === "object" && machineStopReason !== null) {
			const code = (machineStopReason.code as number) ?? 0;
			stopReason = {
				stopCode: code,
				message: (machineStopReason.message as string) ?? "",
				arguments: machineStopReason.contextValue === 0 ? [] : [machineStopReason.contextValue as number],
				isError: code > 1000,
			};
		}
	} else {
		if (Array.isArray(input.activeErrors)) {
			const primaryError = input.activeErrors.find((error: Record<string, unknown>) => error.primary === true);
			if (primaryError && typeof primaryError === "object") {
				stopReason = {
					stopCode: (primaryError.alarm as number) ?? 0,
					message: (primaryError.name as string) ?? "",
					arguments: [primaryError.aux1, primaryError.aux2, primaryError.aux3, primaryError.aux4]
						.filter((a) => a !== undefined && a !== 0)
						.map(Number),
					isError: true,
				};
			}
		}
	}

	let activeWarnings = Array.isArray(input.activeWarnings) ? input.activeWarnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		message: w.name,
		code: w.alarm,
		arguments: [w.aux1, w.aux2, w.aux3, w.aux4].filter((a) => a !== undefined && a !== 0),
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		const activeErrors = Array.isArray(input.activeErrors) ? input.activeErrors : [];

		workstations = [
			{
				number: 1,
				alarms: activeErrors.map((e: Record<string, unknown>) => ({
					code: e.alarm,
					message: e.message ?? "",
					arguments: [e.aux1, e.aux2, e.aux3, e.aux4].filter((a) => a !== undefined && a !== 0).map(String),
				})),
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "X5",
		assetId: input.assetId?.toString() ?? "",
		description: input.description ?? "",
		plcVersion: input.plcVersion ?? "",
		iotDevice,
		stopReason,
		activeWarnings,
		tracks,
		securePrintConfiguration: {
			delayLabelRetrieval: securePrintConfigurationInput?.delayLabelRetrieval ?? false,
		},
		workstations,
		printImageConfiguration,
	};
};

export type PrintImageConfiguration = z.infer<typeof printImageConfigurationSchema>;
export type X5Machine = z.infer<typeof x5MachineSchema>;
